import { ExpandingTooltip } from "./ExpandingTooltip";

export {
  TablePanel as TTTablePanel,
  TablePanelDropdown as TTTablePanelDropdown,
  GenerateReuse as TTGenerateReuse,
  ViewOcr as TTViewOcr,
  UseOffset as TTUseOffset,
  TableClassificationRulesLabel as TTTableClassificationRulesLabel,
  TableStartRule as TTTableStartRule,
  TableSoftStopRule as TTTableSoftStopRule,
  TableHardStopRule as TTTableHardStopRule,
  TableStartRuleReusable as TTTableStartRuleReusable,
  IgnoreRuleCount as TTIgnoreRuleCount,
  IgnoreRuleLabel as TTIgnoreRuleLabel,
  AddRule as TTIgnoreRuleButton,
};

// Item 50
const TablePanel: ExpandingTooltip = () => {
  return (
    <p>
      Configure the rules to extract line-item data from tables and, optionally,
      transform and/or validate the extracted data.
    </p>
  );
};

// Item 51
const TablePanelDropdown: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Select to configure data extraction, validation, or transformation for
        tables.{" "}
      </p>
    );
  return (
    <>
      <p>
        Extraction is selected by default. Configure data extraction rules using
        this interface. Configure template-specific validation rules by
        selecting Validation or template-specific transformation rules by
        selecting Transformation.
      </p>
      <p>
        Each column in a template will inherit any validation and transformation
        rules that have been configured for this Document Type in Batch Class
        Settings. Only configure new extraction or transformation rules that are
        specific to this template. Once configured the template-specific rules
        will override any rules set at the Document Type level.
      </p>
    </>
  );
};

// Item 52
const GenerateReuse: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Click to generate reuse for the document’s table if not already
        displayed.
      </p>
    );
  return (
    <>
      <p>
        Click the <strong>Generate Reuse</strong> button to generate table
        extraction reuse options for this table. If reuse has already been
        generated for the table, the first (highest ranked) reuse option will be
        displayed, and the button will change to show the number of reuse
        options available. You can click through using the arrows inside the
        button to display the other options. Click through until you find the
        reuse option that returns the best extraction results and use these
        rules as the starting point for your configuration of table extraction
        rules. Modify as required before saving the template.
      </p>
    </>
  );
};

// Item 53
const ViewOcr: ExpandingTooltip = (short) => {
  if (short)
    return <p>Toggle this button to show/hide each line of the OCR. </p>;
  return (
    <>
      <p>
        When you configure any Table Start and Stop Rules, the rules that have
        fired will be highlighted and colour coded in the OCR text.{" "}
      </p>
    </>
  );
};

// Item 54
const UseOffset: ExpandingTooltip = (short) => {
  if (short)
    return <p>Toggle this button to enable/disable table offset logic. </p>;
  return (
    <>
      <p>
        Normally, this button will be set to Off (a cross is displayed in the
        button).
      </p>
      <p>
        When the <strong>Use Offset</strong> button is toggled to On (a tick is
        displayed in the button) the system will automatically calculate the
        column width for the area on the document image that spans the words
        configured in the Table Start Rule. Any documents that are classified
        using this template will use this to adjust the column widths for all
        configured columns, based on the width of each document’s extracted
        Table Start Rules column width relative to the original sample used to
        configure the template.
      </p>
      <p>
        You should turn on Use Offset if the documents from this
        supplier/customer are likely to vary in width (which is normally only
        applicable to scanned documents where the overall size/width of the
        image can vary significantly). When you set this toggle to On, the
        calculated Offset area will be displayed on the document image, and a
        new <strong>View Offset</strong> button will appear. Click this button
        to view/hide the offset area.
      </p>
    </>
  );
};

// Item 55
const TableClassificationRulesLabel: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Configure table <strong>Start and Stop Rules</strong> to define where a
        table starts and ends.
      </p>
    );
  return (
    <>
      <p>
        <strong>Table Start and Stop Rules</strong> are configured to define
        where a table starts, breaks (if it can be a multi-page table) and ends.
        These settings, in combination with Column Rules, enable very effective
        line-item extraction.
      </p>
      <p>
        Each rule is a regex, although most are simply the words that appear on
        the document. If the images are likely to be poor quality, configure a
        Fuzzy rule rather than a normal Regex and adjust the fuzzy % as
        appropriate.
      </p>
    </>
  );
};

// Item 56
const TableStartRule: ExpandingTooltip = (short) => {
  if (short) return <p>Configure a rule to define the start of the table. </p>;
  return (
    <>
      <p>
        The <strong>Table Start Rule</strong> defines the row in the OCR where
        Accelerator will set the start of the table and thereafter begin looking
        for the first line of data to be extracted. The row selected by this
        rule is normally the row that contains the column headings. The row must
        not contain any line-item data that needs to be extracted. Set this rule
        as a fuzzy rule if the OCR quality could be poor.
      </p>
    </>
  );
};
// Item 57
const TableSoftStopRule: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Configure a rule to define the end of a table or where a table can break
        in multi-page tables.{" "}
      </p>
    );
  return (
    <>
      <p>
        <strong>Soft Stop</strong> is the most used stop rule. They are
        essential if the table can span multiple pages. Accelerator will extract
        rows of data until it reaches a row with a Soft Stop Rule. It will then
        stop extracting until it finds a new Start Rule, at which point it will
        continue to extract rows until it hits a row with a Soft Stop Rule or a
        Hard Stop Rule. If no Start Rule is found after a Soft Stop Rule fires,
        no more data will be extracted from the table.
      </p>
    </>
  );
};
// Item 58
const TableHardStopRule: ExpandingTooltip = (short) => {
  if (short) return <p>Configure a rule to define the end of a table. </p>;
  return (
    <>
      <p>
        A <strong>Hard Stop</strong> rule is used to tell Accelerator to stop
        line-item extraction. This is a hard stop and is mainly used when the
        supplier/customer always includes the same data at the end of their
        table (e.g., Ts & Cs) that should not be extracted.
      </p>
    </>
  );
};

// Item 59
const TableStartRuleReusable: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Toggle the button to On to allow the Start Rule to be reused for other
        documents that are classified using a General template.
      </p>
    );
  return (
    <>
      <p>
        Setting the Start Rule to be <strong>Reusable</strong> will allow the
        rule to be used as a reuse option for documents that are classified as
        the same document type using a General template. If this Start Rule is
        used via reuse, all Start and Stop Rules, and Column Rules, for this
        template will be used to extract line-item data. If, due to poor quality
        OCR, you have had to set a rule that is not likely to correctly identify
        a table for reuse, set the Reusable button to Off.
      </p>
    </>
  );
};

// Item 60
const IgnoreRuleCount: ExpandingTooltip = () => {
  return <p>The number of Ignore Rules that have been configured.</p>;
};

// Item 61
const IgnoreRuleLabel: ExpandingTooltip = (short) => {
  if (short) return <p>Add rules to ignore rows in tables. </p>;
  return (
    <>
      <p>
        Use the <strong>Add Rule</strong> button to add Ignore Rules. These
        rules are used to configure Accelerator to ignore specific lines within
        a table. Accelerator will ignore any lines that contains the Ignore Rule
        regex. A common example is Subtotal, but any text that commonly appears
        in the table that should not be extracted can be excluded from
        extraction using one or more rules.
      </p>
    </>
  );
};

// Item 62
const AddRule: ExpandingTooltip = () => {
  return <p>Click to add an Ignore Rule. </p>;
};
